import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Radio,
  Wifi,
  Clock,
  Activity,
  ArrowLeft,
  RefreshCw,
  Key,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Timer,
} from "lucide-react";
import Header from "./Header";
import PipelineStatusPanel from "./PipelineStatusPanel";
import OpsControlPanel from "./OpsControlPanel";
import useDashboardData from "@/hooks/useDashboardData";
import { logger } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/sentinel`;

// ─── Utility functions ───────────────────────────────────────────────────

function formatUptime(seconds) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function timeAgo(isoString) {
  if (!isoString) return "never";
  const diff = (Date.now() - new Date(isoString).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function isStale(isoString) {
  if (!isoString) return true;
  return (Date.now() - new Date(isoString).getTime()) / 1000 > 300;
}

function serverStartFromUptime(uptimeSeconds) {
  return new Date(Date.now() - uptimeSeconds * 1000);
}

// ─── Reusable sub-components ─────────────────────────────────────────────

function StatusDot({ active, color }) {
  const c = color || (active ? "#22c55e" : "#ef4444");
  return (
    <span
      className={active ? "cw-live-dot" : ""}
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: c,
        flexShrink: 0,
      }}
    />
  );
}

function MetricCard({ icon: Icon, label, value, subValue, status, children, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg border p-4"
      style={{ backgroundColor: "#161b22", borderColor: "#30363d" }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} style={{ color: "#8b949e" }} />
        <span className="font-mono text-[10px] tracking-[0.12em] uppercase" style={{ color: "#8b949e" }}>
          {label}
        </span>
        {status !== undefined && <StatusDot active={status} />}
      </div>
      {value !== undefined && (
        <div className="font-mono text-2xl font-semibold mb-1" style={{ color: "#e6edf3" }}>
          {value}
        </div>
      )}
      {subValue && (
        <div className="font-mono text-xs" style={{ color: "#8b949e" }}>
          {subValue}
        </div>
      )}
      {children}
    </div>
  );
}

function KeyValueRow({ label, value, valueColor, icon: Icon }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="font-mono text-xs" style={{ color: "#8b949e" }}>
        {Icon && <Icon size={11} className="inline mr-1.5" />}
        {label}
      </span>
      <span className="font-mono text-xs font-medium" style={{ color: valueColor || "#c9d1d9" }}>
        {value}
      </span>
    </div>
  );
}

// ─── OPS Gate Screen ─────────────────────────────────────────────────────

function OpsGateScreen({ onAuthenticated, onBack }) {
  const [opsPassword, setOpsPassword] = useState("");
  const [opsError, setOpsError] = useState("");

  const handleOpsLogin = async (e) => {
    e.preventDefault();
    setOpsError("");
    try {
      await axios.post(`${API}/ops/auth`, { password: opsPassword });
      setOpsPassword("");
      onAuthenticated();
    } catch (err) {
      setOpsError("Wrong password");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#0d1117" }}>
      <div className="w-full" style={{ maxWidth: 380 }}>
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center rounded-full mb-4"
            style={{
              width: 64, height: 64,
              backgroundColor: "rgba(245, 158, 11, 0.08)",
              border: "1px solid rgba(245, 158, 11, 0.25)",
            }}
          >
            <Key size={28} style={{ color: "#f59e0b" }} />
          </div>
          <h1
            className="font-mono text-lg font-bold tracking-[0.12em] uppercase mb-2"
            style={{ color: "#e6edf3" }}
            data-testid="ops-gate-title"
          >
            OPS TERMINAL
          </h1>
          <p className="font-mono text-xs" style={{ color: "#484f58" }}>
            Restricted access. Authenticate to continue.
          </p>
        </div>

        <form onSubmit={handleOpsLogin} className="px-6">
          <input
            data-testid="ops-password-input"
            type="password"
            value={opsPassword}
            onChange={(e) => setOpsPassword(e.target.value)}
            placeholder="Enter OPS password"
            autoFocus
            className="w-full px-4 py-3 rounded-lg font-mono text-sm outline-none transition-all duration-150 mb-3"
            style={{
              backgroundColor: "#161b22",
              color: "#e6edf3",
              border: `1px solid ${opsError ? "rgba(239,68,68,0.5)" : "#30363d"}`,
              letterSpacing: "0.2em",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#f59e0b"; e.target.style.boxShadow = "0 0 0 2px rgba(245,158,11,0.15)"; }}
            onBlur={(e) => { e.target.style.borderColor = "#30363d"; e.target.style.boxShadow = "none"; }}
          />
          <button
            data-testid="ops-login-btn"
            type="submit"
            className="w-full py-2.5 rounded-lg font-mono text-xs font-semibold tracking-[0.1em] uppercase transition-all duration-150"
            style={{
              backgroundColor: "rgba(245, 158, 11, 0.12)",
              color: "#f59e0b",
              border: "1px solid rgba(245, 158, 11, 0.35)",
            }}
          >
            UNLOCK
          </button>
          {opsError && (
            <p className="mt-3 text-center font-mono text-xs" style={{ color: "#ef4444" }} data-testid="ops-error-message">
              {opsError}
            </p>
          )}
        </form>

        <div className="mt-8 text-center">
          <button
            data-testid="ops-back-btn"
            onClick={onBack}
            className="font-mono text-xs transition-colors duration-150"
            style={{ color: "#484f58", background: "none", border: "none", cursor: "pointer" }}
          >
            BACK TO FEED
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Metrics Grid ────────────────────────────────────────────────────────

function MetricsGrid({ metrics }) {
  const m = metrics;
  const stream = m?.stream || {};
  const ingestion = m?.ingestion || {};
  const apiKeys = m?.api_keys || {};

  return (
    <>
      {/* Row 1: Primary status cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-3 sm:mb-4">
        <MetricCard icon={Radio} label="Stream" status={stream.is_running} testId="metric-stream">
          <div className="font-mono text-xl font-semibold mb-1" style={{
            color: stream.is_running ? "#22c55e" : "#ef4444",
          }}>
            {stream.is_running ? "LIVE" : "STOPPED"}
          </div>
          <div className="font-mono text-[10px]" style={{ color: "#8b949e" }}>
            {stream.is_running ? "Webhook active" : "Manual stop"} | {m.mode}
          </div>
        </MetricCard>

        <MetricCard
          icon={TrendingUp}
          label="Ingestion Rate"
          value={`${ingestion.tweets_per_min}/min`}
          subValue={`${ingestion.tweets_per_hour} tweets/hr`}
          testId="metric-ingestion-rate"
        />

        <MetricCard
          icon={Wifi}
          label="Webhook"
          value={stream.is_running ? "Active" : "Paused"}
          subValue="Primary ingestion"
          status={stream.is_running}
          testId="metric-webhook-status"
        />

        <MetricCard
          icon={Timer}
          label="Uptime"
          value={formatUptime(m.uptime_seconds)}
          subValue={`Since ${new Date(serverStartFromUptime(m.uptime_seconds)).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true })} ET`}
          testId="metric-uptime"
        />
      </div>

      {/* Row 2: Ingestion detail + Last Tweet */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div className="rounded-lg border p-4" data-testid="metric-ingestion-detail"
          style={{ backgroundColor: "#161b22", borderColor: "#30363d" }}>
          <div className="flex items-center gap-2 mb-3">
            <Activity size={14} style={{ color: "#8b949e" }} />
            <span className="font-mono text-[10px] tracking-[0.12em] uppercase" style={{ color: "#8b949e" }}>
              Ingestion Pipeline
            </span>
          </div>
          <div className="space-y-0.5">
            <KeyValueRow label="Total Posts" value={ingestion.total_posts} />
            <KeyValueRow label="Analyzed" value={ingestion.analyzed_posts} valueColor="#22c55e" />
            <KeyValueRow label="Errors" value={ingestion.error_posts} valueColor={ingestion.error_posts > 0 ? "#ef4444" : "#8b949e"} />
            <KeyValueRow label="Session Posts" value={ingestion.session_posts} />
            <div className="border-t my-2" style={{ borderColor: "#21262d" }} />
            <KeyValueRow label="Last 1m" value={ingestion.tweets_last_1m} />
            <KeyValueRow label="Last 5m" value={ingestion.tweets_last_5m} />
            <KeyValueRow label="Last 15m" value={ingestion.tweets_last_15m} />
            <KeyValueRow label="Last 1h" value={ingestion.tweets_last_1h} />
          </div>
        </div>

        <div className="rounded-lg border p-4" data-testid="metric-last-tweet"
          style={{ backgroundColor: "#161b22", borderColor: "#30363d" }}>
          <div className="flex items-center gap-2 mb-3">
            <Clock size={14} style={{ color: "#8b949e" }} />
            <span className="font-mono text-[10px] tracking-[0.12em] uppercase" style={{ color: "#8b949e" }}>
              Last Tweet Ingested
            </span>
            {ingestion.last_tweet_at && (
              <span className="font-mono text-[10px] ml-auto" style={{ color: isStale(ingestion.last_tweet_at) ? "#f59e0b" : "#22c55e" }}>
                {timeAgo(ingestion.last_tweet_at)}
              </span>
            )}
          </div>
          {ingestion.last_tweet_at ? (
            <div className="space-y-2">
              <div className="font-mono text-sm font-medium" style={{ color: "#e6edf3" }}>
                {ingestion.last_tweet_handle}
              </div>
              <div className="text-sm leading-relaxed" style={{ color: "#c9d1d9", fontFamily: "Inter, sans-serif" }}>
                {ingestion.last_tweet_preview}
              </div>
              <div className="font-mono text-[10px]" style={{ color: "#484f58" }}>
                {new Date(ingestion.last_tweet_at).toLocaleString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true, month: "short", day: "numeric" })} ET
              </div>
            </div>
          ) : (
            <div className="font-mono text-xs" style={{ color: "#484f58" }}>
              No tweets ingested yet
            </div>
          )}
        </div>
      </div>

      {/* Row 3: API Keys */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div className="rounded-lg border p-4" data-testid="metric-api-keys"
          style={{ backgroundColor: "#161b22", borderColor: "#30363d" }}>
          <div className="flex items-center gap-2 mb-3">
            <Key size={14} style={{ color: "#8b949e" }} />
            <span className="font-mono text-[10px] tracking-[0.12em] uppercase" style={{ color: "#8b949e" }}>
              API Keys
            </span>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              {apiKeys.emergent_llm_key ? (
                <CheckCircle2 size={13} style={{ color: "#22c55e" }} />
              ) : (
                <XCircle size={13} style={{ color: "#ef4444" }} />
              )}
              <span className="font-mono text-xs" style={{ color: apiKeys.emergent_llm_key ? "#c9d1d9" : "#ef4444" }}>
                Emergent LLM Key
              </span>
            </div>
            <div className="flex items-center gap-2">
              {apiKeys.twitterapi_io_key ? (
                <CheckCircle2 size={13} style={{ color: "#22c55e" }} />
              ) : (
                <XCircle size={13} style={{ color: "#ef4444" }} />
              )}
              <span className="font-mono text-xs" style={{ color: apiKeys.twitterapi_io_key ? "#c9d1d9" : "#ef4444" }}>
                TwitterAPI.io Key
              </span>
            </div>
          </div>
          <div className="mt-3">
            <KeyValueRow label="Deep Analysis Cache" value={`${m.database?.deep_analysis_cached ?? 0} cached`} />
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Main Dashboard Component ────────────────────────────────────────────

export default function Dashboard() {
  const [opsAuthed, setOpsAuthed] = useState(false);
  const [opsChecking, setOpsChecking] = useState(true);
  const navigate = useNavigate();

  const {
    metrics, loading, error, lastRefresh,
    autoRefresh, toggleAutoRefresh,
    ipLog, pipelineStatus,
    fetchIpLog, fetchPipelineStatus,
  } = useDashboardData();

  // Check OPS IP auth on mount
  useEffect(() => {
    const checkIp = async () => {
      try {
        const res = await axios.get(`${API}/ops/check-ip`);
        if (res.data.authorized) {
          setOpsAuthed(true);
        }
      } catch (err) {
        logger.debug("[Dashboard] IP check:", err.message);
      }
      setOpsChecking(false);
    };
    checkIp();
  }, []);

  // OPS-specific polling (pipeline + IP log)
  useEffect(() => {
    if (!opsAuthed) return;
    fetchPipelineStatus();
    fetchIpLog();
    const pipelineInterval = setInterval(fetchPipelineStatus, 10000);
    const ipInterval = setInterval(fetchIpLog, 10000);
    return () => {
      clearInterval(pipelineInterval);
      clearInterval(ipInterval);
    };
  }, [opsAuthed, fetchPipelineStatus, fetchIpLog]);

  const handleRetryErrors = async () => {
    try {
      const res = await axios.post(`${API}/pipeline/retry-errors`);
      alert(`Re-queued ${res.data.requeued} errored posts`);
      fetchPipelineStatus();
    } catch (err) {
      alert("Retry failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleClearQueue = async () => {
    if (!window.confirm("This will skip ALL queued posts. Are you sure?")) return;
    try {
      const res = await axios.post(`${API}/pipeline/clear-queue`);
      alert(`Cleared ${res.data.cleared} posts from queue`);
      fetchPipelineStatus();
    } catch (err) {
      alert("Clear failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleOpsAuthenticated = useCallback(() => {
    setOpsAuthed(true);
    fetchIpLog();
  }, [fetchIpLog]);

  // ─── Loading state ─────────────────────────────────────────────────────
  if (opsChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#0d1117" }}>
        <div style={{ width: 32, height: 32, border: '2px solid #30363d', borderTopColor: '#f59e0b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ─── OPS Password Gate ─────────────────────────────────────────────────
  if (!opsAuthed) {
    return <OpsGateScreen onAuthenticated={handleOpsAuthenticated} onBack={() => navigate("/")} />;
  }

  // ─── Main Dashboard ────────────────────────────────────────────────────
  const m = metrics;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#0d1117" }}>
      <Header aggregation={null} postCount={0} connected={true} mode={m?.mode} />

      <div className="max-w-[1200px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              data-testid="dashboard-back-btn"
              onClick={() => navigate("/")}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded font-mono text-xs transition-colors duration-150"
              style={{ color: "#8b949e", border: "1px solid #30363d", backgroundColor: "transparent" }}
            >
              <ArrowLeft size={13} />
              FEED
            </button>
            <h2
              data-testid="dashboard-title"
              className="font-mono text-sm sm:text-base font-semibold tracking-tight"
              style={{ color: "#e6edf3" }}
            >
              <span className="hidden sm:inline">SYSTEM DASHBOARD</span>
              <span className="sm:hidden">DASHBOARD</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="auto-refresh-toggle"
              onClick={toggleAutoRefresh}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded font-mono text-[10px] tracking-[0.08em] uppercase transition-colors duration-150"
              style={{
                backgroundColor: autoRefresh ? "rgba(34, 197, 94, 0.1)" : "rgba(239, 68, 68, 0.1)",
                color: autoRefresh ? "#22c55e" : "#ef4444",
                border: `1px solid ${autoRefresh ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
              }}
            >
              <RefreshCw size={11} className={autoRefresh ? "cw-spin" : ""} style={autoRefresh ? { animationDuration: "3s" } : {}} />
              {autoRefresh ? "LIVE" : "PAUSED"}
            </button>
            {lastRefresh && (
              <span className="font-mono text-[10px]" style={{ color: "#484f58" }}>
                {lastRefresh.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit", hour12: true })} ET
              </span>
            )}
          </div>
        </div>

        {error && (
          <div
            className="flex items-center gap-2 mb-4 px-4 py-2.5 rounded-lg border font-mono text-xs"
            style={{
              backgroundColor: "rgba(239, 68, 68, 0.08)",
              borderColor: "rgba(239, 68, 68, 0.3)",
              color: "#ef4444",
            }}
          >
            <AlertTriangle size={13} />
            Failed to fetch metrics: {error}
          </div>
        )}

        {loading && !m ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={20} className="cw-spin" style={{ color: "#8b949e" }} />
          </div>
        ) : m ? (
          <MetricsGrid metrics={m} />
        ) : null}

        <PipelineStatusPanel
          pipelineStatus={pipelineStatus}
          onRetryErrors={handleRetryErrors}
          onClearQueue={handleClearQueue}
        />

        <OpsControlPanel
          ipLog={ipLog}
          onIpLogRefresh={fetchIpLog}
        />
      </div>
    </div>
  );
}
